import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { fmtEUR, fmtDate } from "../../lib/format";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { FileText, Sparkles, Save, Receipt } from "lucide-react";
import { toast } from "sonner";
import InvoiceForm from "../invoices/InvoiceForm";

const F = ({ label, value, onChange, testid }) => (
  <div><Label className="text-[11px]">{label}</Label><Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" data-testid={testid} /></div>
);

export default function DatiRogitoPanel({ unit, canWrite, onChanged, fatturaRiga }) {
  const [dr, setDr] = useState(unit?.dati_rogito || null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bozza, setBozza] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [operations, setOperations] = useState([]);
  useEffect(() => { setDr(unit?.dati_rogito || null); }, [unit]);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); api.get("/operations").then((r) => setOperations(r.data)).catch(() => {}); }, []);

  useEffect(() => { if (fatturaRiga?.id) emetti(fatturaRiga.id); }, [fatturaRiga]); // eslint-disable-line react-hooks/exhaustive-deps

  const leggi = async () => {
    if (!file) return toast.error("Scegli il PDF dell'atto o del certificato di rogito");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/units/${unit.id}/rogito/leggi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null); toast.message("Lettura AI avviata: 1-2 minuti, la pagina si aggiorna da sola");
      const t0 = Date.now();
      const poll = async () => {
        const r = await api.get(`/units/${unit.id}`);
        const st = r.data.rogito_lettura?.stato;
        if (st === "ok") { setDr(r.data.dati_rogito); setBusy(false); onChanged?.(); toast.success(`Atto letto (${r.data.dati_rogito?.pagine_lette || "?"} pagine): controlla e correggi i dati`); return; }
        if (st === "errore") { setBusy(false); toast.error("Lettura AI non riuscita: " + (r.data.rogito_lettura.errore || "")); return; }
        if (Date.now() - t0 > 240000) { setBusy(false); toast.error("Lettura ancora in corso: ricarica la pagina tra poco"); return; }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 5000);
    } catch (e) { setBusy(false); toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const salva = async () => {
    try { await api.put(`/units/${unit.id}/rogito`, dr); toast.success("Dati rogito salvati"); onChanged?.(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const emetti = async (riga_id) => {
    try { const r = await api.post(`/units/${unit.id}/fattura-bozza`, { riga_id: riga_id || null }); if (r.data.avvertenze?.length) toast.message("Controlla: " + r.data.avvertenze.join(" · ")); setBozza(r.data); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const acq = dr?.acquirenti?.[0] || {};
  const setAcq = (k, v) => setDr({ ...dr, acquirenti: [{ ...acq, [k]: v }, ...(dr?.acquirenti || []).slice(1)] });
  const pc = dr?.parti_comuni || {};
  const setPc = (k, v) => setDr({ ...dr, parti_comuni: { ...pc, [k]: v } });

  return (
    <Card className="p-4 space-y-3" data-testid="dati-rogito">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Dati rogito / acquirente {dr?.letto_il && <Badge variant="outline" className="text-[10px]">letto il {fmtDate(dr.letto_il)}</Badge>}</div>
        <Button size="sm" onClick={() => emetti()} disabled={!canWrite} data-testid="unit-emetti-fattura"><Receipt className="w-4 h-4 mr-1" /> Emetti fattura</Button>
      </div>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files[0])} className="text-xs" data-testid="rogito-file" />
          <Button size="sm" variant="outline" onClick={leggi} disabled={busy || !file} data-testid="rogito-leggi"><Sparkles className="w-4 h-4 mr-1" /> {busy ? "Lettura in corso..." : "Leggi atto con AI"}</Button>
          <span className="text-[11px] text-muted-foreground">Atto di compravendita o certificato di avvenuta stipula: estrae acquirente, catastali, parti comuni, prezzi (se presenti).</span>
        </div>
      )}
      {dr && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <F label="Acquirente (denominazione)" value={acq.denominazione || (acq.nome || acq.cognome ? `${acq.nome || ""} ${acq.cognome || ""}`.trim() : "")} onChange={(v) => setAcq("denominazione", v)} testid="rog-acq" />
            <F label="Codice fiscale" value={acq.codice_fiscale} onChange={(v) => setAcq("codice_fiscale", v)} />
            <F label="Partita IVA" value={acq.partita_iva} onChange={(v) => setAcq("partita_iva", v)} />
            <F label="Indirizzo" value={acq.indirizzo} onChange={(v) => setAcq("indirizzo", v)} />
            <F label="CAP" value={acq.cap} onChange={(v) => setAcq("cap", v)} />
            <F label="Comune" value={acq.comune} onChange={(v) => setAcq("comune", v)} />
            <F label="Prov." value={acq.provincia} onChange={(v) => setAcq("provincia", v)} />
            <F label="Data atto" value={dr.data_atto} onChange={(v) => setDr({ ...dr, data_atto: v })} />
            <F label="Notaio" value={dr.notaio} onChange={(v) => setDr({ ...dr, notaio: v })} />
            <F label="Repertorio" value={dr.repertorio} onChange={(v) => setDr({ ...dr, repertorio: v })} />
            <F label="Prezzo totale €" value={dr.prezzo_totale} onChange={(v) => setDr({ ...dr, prezzo_totale: v === "" ? null : parseFloat(v) })} />
            <F label="Regime IVA (aliquota)" value={dr.iva?.aliquota} onChange={(v) => setDr({ ...dr, iva: { ...(dr.iva || {}), aliquota: v === "" ? null : parseFloat(v) } })} />
          </div>
          <div>
            <div className="text-[11px] font-medium text-muted-foreground mb-1">Dati catastali</div>
            <div className="flex flex-wrap gap-1">{(dr.catastali || []).map((c, i) => <Badge key={i} variant="outline" className="text-[11px] font-normal">{c.tipo && c.tipo !== "principale" ? `${c.tipo}: ` : ""}Fg. {c.foglio || "—"} Part. {c.particella || "—"} Sub. {c.subalterno || "—"}{c.categoria ? ` cat. ${c.categoria}` : ""}</Badge>)}{!(dr.catastali || []).length && <span className="text-xs text-muted-foreground">—</span>}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="col-span-2"><F label="Parti comuni / disimpegno (descrizione)" value={pc.descrizione} onChange={(v) => setPc("descrizione", v)} testid="rog-pc" /></div>
            <F label="Millesimi" value={pc.quota_millesimi} onChange={(v) => setPc("quota_millesimi", v)} />
            <F label="Prezzo parti comuni € (vuoto = compreso)" value={pc.prezzo_indicato} onChange={(v) => setPc("prezzo_indicato", v === "" ? null : parseFloat(v))} />
          </div>
          {(dr.acconti_dichiarati || []).length > 0 && <div className="text-xs text-muted-foreground">Acconti dichiarati in atto: {dr.acconti_dichiarati.map((a) => `${fmtEUR(a.importo)} (${a.data || "—"})`).join(" · ")}</div>}
          {(dr.avvertenze || []).length > 0 && <div className="text-xs text-amber-700 bg-amber-50 rounded p-2">{dr.avvertenze.join(" · ")}</div>}
          {canWrite && <div className="flex justify-end"><Button size="sm" variant="outline" onClick={salva} data-testid="rogito-salva"><Save className="w-4 h-4 mr-1" /> Salva dati rogito</Button></div>}
        </div>
      )}
      {!dr && <p className="text-xs text-muted-foreground">Nessun dato rogito. Carica l'atto per precompilare la fattura di vendita, oppure premi «Emetti fattura» per partire dai dati dell'unità.</p>}
      <InvoiceForm open={!!bozza} onClose={() => setBozza(null)} initial={bozza} companies={companies} operations={operations} onSaved={() => { setBozza(null); onChanged?.(); }} />
    </Card>
  );
}
